import React, { useState, useEffect } from 'react';
import { ScrollView, TextInput, Alert, ActivityIndicator, TouchableOpacity, Image } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { Calendar, MapPin, Clock, Banknote, Star, BadgeCheck } from 'lucide-react-native';
import { CustomButton, Screen, ScreenHeader, AuthenticatedImage } from '@/components';
import { COLORS } from '@/constants/colors';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '@/types/navigation';
import { useMeetingAnnouncement } from '@/api/hooks/useMeetingAnnouncements';
import { useCreateApplication } from '@/api/hooks/useMeetingApplications';
import { usePublicProfile, useMyProfile } from '@/api/hooks/useProfile';
import { showWarningToast, showErrorToast, showSuccessToast } from '@/utils/toast';
import { chatService } from '@/services/firebase';

type ApplicationCreateRouteProp = RouteProp<RootStackParamList, 'ApplicationCreate'>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const MAX_MOTIVATION_LENGTH = 300;

const ApplicationCreateScreen: React.FC = () => {
  const themeName = useThemeName();
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<ApplicationCreateRouteProp>();
  const { announcementId, announcement: passedAnnouncement } = route.params;

  const [motivation, setMotivation] = useState('');
  const [proposedAmount, setProposedAmount] = useState<string>('');
  const [acceptsOriginalPrice, setAcceptsOriginalPrice] = useState(true);
  
  // Get current user profile for applicant ID
  const { data: myProfile } = useMyProfile();

  const bgColor = themeName === 'dark' ? COLORS.BLACK : '#f5f5f5';
  const titleColor = themeName === 'dark' ? '#ffffff' : '#18181b';
  const subtitleColor = themeName === 'dark' ? '#a1a1aa' : '#71717a';
  const softBorder = themeName === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const inputBg = themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const placeholderColor = themeName === 'dark' ? '#71717a' : '#a1a1aa';

  // Always fetch announcement data from API based on URL ID
  // TODO: Uncomment when API endpoint is available
  // const { 
  //   data: announcement, 
  //   isLoading: isLoadingAnnouncement,
  //   refetch
  // } = useMeetingAnnouncement(announcementId, {
  //   enabled: !!announcementId, // Always fetch if announcementId is available
  //   // Force refetch on mount and window focus to always get fresh data
  //   refetchOnMount: true,
  //   refetchOnWindowFocus: true,
  //   staleTime: 0, // Data is immediately stale, so it will refetch
  // });

  // Force refetch when component mounts or announcementId changes
  // useEffect(() => {
  //   if (announcementId) {
  //     refetch();
  //   }
  // }, [announcementId, refetch]);

  // Use passed announcement from navigation (no API call for now)
  const announcement = passedAnnouncement;
  const isLoadingAnnouncement = false;

  // Fetch public profile of the announcement creator for rating data
  const { data: creatorProfile } = usePublicProfile(
    announcement?.createdById || '',
    { enabled: !!announcement?.createdById }
  );

  const { mutate: createApplication, isPending } = useCreateApplication({
    onSuccess: async (application) => {
      // Send Firebase notification to announcement owner for real-time update
      if (announcement?.createdById && myProfile?.id) {
        try {
          await chatService.sendApplicationNotification(
            announcement.createdById, // hostUserId
            application.id,           // applicationId
            announcementId,           // announcementId
            myProfile.id              // applicantId
          );
        } catch (error) {
          console.error('Failed to send Firebase notification:', error);
          // Don't block navigation on notification failure
        }
      }
      
      navigation.navigate('ApplicationSent', {
        announcementId,
        announcement,
        application,
      });
    },
    onError: (error) => {
      showErrorToast(t('common.error'), error.message);
    },
  });

  const handleSubmit = () => {
    const trimmedMotivation = motivation.trim();
    
    if (!trimmedMotivation) {
      showWarningToast(t('screens.applicationCreate.motivationRequired', 'Napisz dlaczego chcesz dołączyć'));
      return;
    }

    if (trimmedMotivation.length < 10) {
      showWarningToast(t('screens.applicationCreate.motivationTooShort', 'Motywacja jest zbyt krótka (min. 10 znaków)'));
      return;
    }
    
    // Validate proposed amount if user chose to propose different amount
    const originalPrice = announcement?.paymentAmount ? parseFloat(announcement.paymentAmount) : 0;
    const isPaidAnnouncement = originalPrice > 0;
    
    if (!acceptsOriginalPrice && isPaidAnnouncement) {
      if (!proposedAmount || proposedAmount.trim() === '') {
        showWarningToast(t('screens.applicationCreate.enterProposedAmount', 'Podaj proponowaną kwotę'));
        return;
      }
      const amount = parseFloat(proposedAmount);
      if (isNaN(amount) || amount < 0) {
        showWarningToast(t('screens.applicationCreate.invalidAmount', 'Podaj prawidłową kwotę'));
        return;
      }
    }

    const data = {
      motivation: trimmedMotivation,
      proposedAmount: acceptsOriginalPrice ? null : (proposedAmount ? parseFloat(proposedAmount) : null),
    };

    createApplication({ announcementId, data });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const months = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 
                    'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];
    return `${day} ${months[date.getMonth()]}`;
  };

  const formatTime = (timeStr: string | null | undefined) => {
    if (!timeStr) return '';
    // Handle both HH:MM:SS and HH:MM formats
    return timeStr.substring(0, 5);
  };

  // Loading state
  if (isLoadingAnnouncement) {
    return (
      <Screen edges={['top']}>
        <YStack flex={1} backgroundColor={bgColor} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={titleColor} />
        </YStack>
      </Screen>
    );
  }

  // Error state
  if (!announcement) {
    return (
      <Screen edges={['top']}>
        <YStack flex={1} backgroundColor={bgColor} alignItems="center" justifyContent="center" padding="$4">
          <Text color={titleColor}>{t('screens.applicationCreate.announcementNotFound')}</Text>
        </YStack>
      </Screen>
    );
  }

  const originalPrice = announcement.paymentAmount ? parseFloat(announcement.paymentAmount) : 0;
  const isFreeAnnouncement = originalPrice === 0;
  const displayName = announcement.createdByFirstName || announcement.createdByNickname || 'Anonim';
  const meetingTitle = announcement.meetingType?.name || t('screens.applicationCreate.meetingTitle');

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        <ScreenHeader
          title={t('screens.applicationCreate.title')}
          onBackPress={() => navigation.goBack()}
        />

        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 100 }} showsVerticalScrollIndicator={false}>
        <YStack backgroundColor="$backgroundStrong" borderRadius="$6" padding="$5" space="$4" borderWidth={1} borderColor={softBorder}>
          
          {/* User Info Section */}
          <XStack space="$3" alignItems="center">
            <YStack width={60} height={60} borderRadius={30} overflow="hidden" backgroundColor={inputBg}>
              {announcement.createdByPhotoThumbnail ? (
                <AuthenticatedImage 
                  uri={announcement.createdByPhotoThumbnail} 
                  style={{ width: 60, height: 60 }}
                />
              ) : (
                <YStack flex={1} alignItems="center" justifyContent="center">
                  <Text fontSize={24} color={subtitleColor}>{displayName.charAt(0).toUpperCase()}</Text>
                </YStack>
              )}
            </YStack>
            <YStack flex={1}>
              <XStack alignItems="center" space="$1">
                <Text fontSize={18} fontWeight="600" color={titleColor}>
                  {displayName}
                </Text>
                {creatorProfile?.isVerified && (
                  <BadgeCheck size={18} color="#22c55e" />
                )}
              </XStack>
              {creatorProfile?.age && (
                <Text fontSize={14} color={subtitleColor}>
                  {creatorProfile.age} {t('screens.applicationCreate.years')}
                </Text>
              )}
              <XStack alignItems="center" space="$1">
                <Star size={14} color="#fbbf24" fill="#fbbf24" />
                <Text fontSize={14} color={subtitleColor}>
                  {creatorProfile 
                    ? `${creatorProfile.ratingAverage.toFixed(1)} (${creatorProfile.ratingCount} ${t('screens.applicationCreate.reviews')})`
                    : t('screens.applicationCreate.noRating', 'Brak ocen')
                  }
                </Text>
              </XStack>
            </YStack>
          </XStack>

          {/* Meeting Title */}
          <Text fontSize={18} fontWeight="600" color={titleColor}>
            {meetingTitle}
          </Text>

          {/* Meeting Info Row 1 */}
          <XStack justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" space="$2">
              <Calendar size={18} color={subtitleColor} />
              <Text fontSize={14} color={subtitleColor}>
                {formatDate(announcement.meetingDate)}, {formatTime(announcement.meetingTime)}
              </Text>
            </XStack>
            <XStack alignItems="center" space="$2">
              <MapPin size={18} color={subtitleColor} />
              <Text fontSize={14} color={subtitleColor}>
                {announcement.meetingAddress}
              </Text>
            </XStack>
          </XStack>

          {/* Meeting Info Row 2 */}
          <XStack justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" space="$2">
              <Clock size={18} color={subtitleColor} />
              {/* TODO: API - add meetingDuration field to MeetingAnnouncementView */}
              <Text fontSize={14} color={subtitleColor}>
                3-4 {t('screens.applicationCreate.hours')}
              </Text>
            </XStack>
            <XStack alignItems="center" space="$2">
              <Banknote size={18} color={subtitleColor} />
              <Text fontSize={14} color={titleColor} fontWeight="600">
                {originalPrice} zł
              </Text>
            </XStack>
          </XStack>

          {/* About Meeting Section */}
          <YStack backgroundColor={inputBg} borderRadius="$4" padding="$4" space="$2">
            <Text fontSize="$4" fontWeight="600" color={titleColor}>
              {t('screens.applicationCreate.aboutMeeting')}
            </Text>
            <Text fontSize={14} color={subtitleColor} lineHeight={20}>
              {announcement.additionalInfo || t('screens.applicationCreate.noDescription')}
            </Text>
          </YStack>

          {/* Motivation Section */}
          <YStack space="$2">
            <Text fontSize="$4" fontWeight="600" color={titleColor}>
              {t('screens.applicationCreate.motivationTitle')}
            </Text>
            <TextInput
              style={getMultilineInputStyle(inputBg, softBorder, titleColor)}
              placeholder={t('screens.applicationCreate.motivationPlaceholder')}
              placeholderTextColor={placeholderColor}
              value={motivation}
              onChangeText={(text) => setMotivation(text.slice(0, MAX_MOTIVATION_LENGTH))}
              multiline
              numberOfLines={4}
            />
            <Text fontSize={12} color={subtitleColor} textAlign="right">
              {motivation.length}/{MAX_MOTIVATION_LENGTH}
            </Text>
          </YStack>

          {/* Price Proposal Section - only show for paid announcements */}
          {!isFreeAnnouncement && (
            <YStack space="$3">
              <Text fontSize="$4" fontWeight="600" color={titleColor}>
                {t('screens.applicationCreate.priceProposal')}
              </Text>
              
              <YStack 
                backgroundColor={inputBg} 
                borderRadius="$4" 
                padding="$4" 
                alignItems="center"
                space="$2"
              >
                <Text fontSize={14} color={subtitleColor}>
                  {t('screens.applicationCreate.originalPrice')}
                </Text>
                <Text fontSize={32} fontWeight="bold" color={titleColor}>
                  zł {originalPrice}
                </Text>
              </YStack>

              <XStack space="$3">
                <TouchableOpacity 
                  style={{ flex: 1 }}
                  onPress={() => setAcceptsOriginalPrice(false)}
                >
                  <YStack 
                    backgroundColor={!acceptsOriginalPrice ? '#5E809C' : 'transparent'}
                    borderRadius={12} 
                    paddingVertical={14}
                    alignItems="center"
                    borderWidth={1}
                    borderColor={!acceptsOriginalPrice ? '#5E809C' : softBorder}
                  >
                    <Text 
                      fontSize={14} 
                      color={!acceptsOriginalPrice ? 'white' : titleColor}
                    >
                      {t('screens.applicationCreate.otherAmount')}
                    </Text>
                  </YStack>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={{ flex: 1 }}
                  onPress={() => setAcceptsOriginalPrice(true)}
                >
                  <YStack 
                    backgroundColor={acceptsOriginalPrice ? '#5E809C' : 'transparent'}
                    borderRadius={12} 
                    paddingVertical={14}
                    alignItems="center"
                    borderWidth={1}
                    borderColor={acceptsOriginalPrice ? '#5E809C' : softBorder}
                  >
                    <Text 
                      fontSize={14} 
                      color={acceptsOriginalPrice ? 'white' : titleColor}
                    >
                      {t('screens.applicationCreate.acceptPrice')}
                    </Text>
                  </YStack>
                </TouchableOpacity>
              </XStack>

              {!acceptsOriginalPrice && (
                <TextInput
                  style={getSingleLineInputStyle(inputBg, softBorder, titleColor)}
                  placeholder={t('screens.applicationCreate.enterAmount')}
                  placeholderTextColor={placeholderColor}
                  value={proposedAmount}
                  onChangeText={setProposedAmount}
                  keyboardType="numeric"
                />
              )}
            </YStack>
          )}

          {/* Privacy Notice */}
          <YStack 
            backgroundColor="rgba(234,179,8,0.1)" 
            borderRadius={12} 
            padding="$4" 
            borderWidth={1}
            borderColor="rgba(234,179,8,0.2)"
          >
            <Text fontSize={14} color="#92400e">
              <Text fontWeight="600">{t('screens.applicationCreate.privacyTitle')}</Text>
              {' '}{t('screens.applicationCreate.privacyText')}
            </Text>
          </YStack>

          {/* Submit Button */}
          <CustomButton
            backgroundColor="#5E809C"
            color="white"
            paddingVertical={14}
            borderRadius={12}
            onPress={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <ActivityIndicator color="white" size="small" />
            ) : (
              t('screens.applicationCreate.submit')
            )}
          </CustomButton>
        </YStack>
      </ScrollView>
      </YStack>
    </Screen>
  );
};

// Style functions (same as CreateAnnouncementScreen)
function getSingleLineInputStyle(
  inputBg: string,
  borderColor: string,
  textColor: string
): TextStyle {
  return {
    backgroundColor: inputBg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: borderColor,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 14,
    color: textColor,
  };
}

function getMultilineInputStyle(
  inputBg: string,
  borderColor: string,
  textColor: string
): TextStyle {
  return {
    backgroundColor: inputBg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: borderColor,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    color: textColor,
    minHeight: 80,
    textAlignVertical: 'top',
  };
}

export default ApplicationCreateScreen;




